import React from 'react';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type ArgyleVoieFormValues = {
  apiKey: string;
  apiSecret: string;
};

interface EditArgyleVoieIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditArgyleVoieIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditArgyleVoieIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let apiKey = '';
  let apiSecret = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    apiKey = data.apiKey || '';
    apiSecret = data.apiSecret || '';
  }

  const validationSchema = yup.object().shape({
    apiKey: yup.string().required('API Key is required'),
    apiSecret: yup.string().required('API Secret is required'),
  });

  const { control, handleSubmit, formState } = useForm<ArgyleVoieFormValues>({
    defaultValues: {
      apiKey: apiKey,
      apiSecret: apiSecret,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: ArgyleVoieFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            apiKey: data.apiKey,
            apiSecret: data.apiSecret,
          }),
        };
      } else {
        settings = {
          name: 'Argyle VOIE Integration',
          vendor: IntegrationVendor.Argyle,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            apiKey: data.apiKey,
            apiSecret: data.apiSecret,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Argyle VOIE Settings' : 'Argyle VOIE Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="apiKey"
            label="API Key *"
            placeholder="Enter your Argyle API Key"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="apiSecret"
            label="API Secret *"
            placeholder="Enter your Argyle API Secret"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
